import { useState, useEffect, useCallback, useRef } from 'react';
import { loadPdfDocument, renderPdfPage } from '../utils/pdfUtils';

// The zoom the controls call "100%": 80% of fit-to-width, which leaves a margin
// either side rather than pressing the page against the viewport edges.
const BASELINE_SCALE_FACTOR = 0.8;

// Zoom the form opens at, as a fraction of the 100% baseline above. A whole A4 page
// is unreadable at fit-width on a laptop, so the form starts zoomed out far enough
// to show a section at a glance; the patient can zoom in from there.
const INITIAL_ZOOM_FRACTION = 0.75;

// Detect if the device is an iPad or tablet-sized
const getDeviceInfo = () => {
  const ua = navigator.userAgent || '';
  const isIPad = /iPad/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isTablet = isIPad ||
    (window.innerWidth >= 768 && window.innerWidth <= 1366 && 'ontouchstart' in window);
  return { isIPad, isTablet };
};

const getAvailableViewerWidth = (containerRef) => {
  const containerWidth = containerRef.current?.clientWidth;
  const baseWidth = (containerWidth && containerWidth > 0) ? containerWidth : window.innerWidth;
  const horizontalGutter = Math.min(Math.max(baseWidth * 0.09, 48), 128);

  // Use the real container width so horizontal panning starts from the true left edge,
  // especially on iPad Safari when zooming beyond fit-to-width.
  return Math.max(baseWidth - horizontalGutter * 2, 320);
};

const getStableViewportWidth = (containerRef) => {
  const containerWidth = containerRef.current?.clientWidth;
  if (containerWidth && containerWidth > 0) return containerWidth;

  const visualWidth = window.visualViewport?.width;
  if (visualWidth && visualWidth > 0) return visualWidth;

  return window.innerWidth;
};

export const usePdfRenderer = (pdfUrl) => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfLibDoc, setPdfLibDoc] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(null); // Start as null - wait for auto-fit
  const [autoScale, setAutoScale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const textLayerRef = useRef(null);
  const renderingRef = useRef(false);
  const pendingRenderRef = useRef(null);
  const scaleRef = useRef(null);
  const autoScaleRef = useRef(null);
  const stableViewportWidthRef = useRef(0);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    autoScaleRef.current = autoScale;
  }, [autoScale]);

  // Load PDF document and calculate initial fit scale
  useEffect(() => {
    if (!pdfUrl) return;

    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { pdfDoc, pdfLibDoc, arrayBuffer } = await loadPdfDocument(pdfUrl);
        
        // Calculate fit scale using the actual viewer width
        const page = await pdfDoc.getPage(1);
        const defaultViewport = page.getViewport({ scale: 1.0 });
        const pdfWidth = defaultViewport.width;
        const measuredViewportWidth = getStableViewportWidth(containerRef);
        stableViewportWidthRef.current = measuredViewportWidth;
        const screenWidth = getAvailableViewerWidth(containerRef);
        
        let fitScale = screenWidth / pdfWidth;
        const { isTablet } = getDeviceInfo();
        
        // fitScale is the scale that makes the PDF fit the screen width
        // DPR scaling is handled separately in the rendering components
        console.log('[PDF Renderer] Device info:', {
          screenWidth,
          pdfWidth,
          isTablet,
          pixelRatio: window.devicePixelRatio || 1,
          fitScale
        });
        
        fitScale = Math.max(fitScale, 0.5);
        
        setPdfDoc(pdfDoc);
        setPdfLibDoc(pdfLibDoc);
        setPdfBytes(arrayBuffer);
        setTotalPages(pdfDoc.numPages);
        setCurrentPage(1);
        setAutoScale(fitScale);
        setScale(fitScale * BASELINE_SCALE_FACTOR * INITIAL_ZOOM_FRACTION);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPdf();
  }, [pdfUrl]);

  // Recalculate fit scale on window resize
  useEffect(() => {
    if (!pdfDoc) return;

    const handleResize = async () => {
      try {
        const measuredViewportWidth = getStableViewportWidth(containerRef);
        const previousViewportWidth = stableViewportWidthRef.current || measuredViewportWidth;
        const viewportDelta = Math.abs(measuredViewportWidth - previousViewportWidth);
        const isMinorMobileChromeResize = viewportDelta > 0 && viewportDelta < 120;
        const { isTablet } = getDeviceInfo();

        if (isTablet && isMinorMobileChromeResize) {
          return;
        }

        stableViewportWidthRef.current = measuredViewportWidth;

        const page = await pdfDoc.getPage(1);
        const defaultViewport = page.getViewport({ scale: 1.0 });
        const pdfWidth = defaultViewport.width;
        const screenWidth = getAvailableViewerWidth(containerRef);
        
        let fitScale = screenWidth / pdfWidth;
        fitScale = Math.max(fitScale, 0.5);
        
        const previousScale = scaleRef.current;
        const previousAutoScale = autoScaleRef.current;

        setAutoScale(fitScale);

        // Keep the zoom the reader chose, expressed against the new fit width.
        // Comparing against raw fit-scale would only match a zoom level the form
        // never actually opens at, so a resize would silently jump the page.
        if (previousScale == null || previousAutoScale == null || previousAutoScale === 0) {
          setScale(fitScale * BASELINE_SCALE_FACTOR * INITIAL_ZOOM_FRACTION);
        } else {
          setScale(fitScale * (previousScale / previousAutoScale));
        }
      } catch (err) {
        console.error('Error recalculating fit scale:', err);
      }
    };

    window.addEventListener('resize', handleResize);
    const handleOrientation = () => setTimeout(handleResize, 200);
    window.addEventListener('orientationchange', handleOrientation);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientation);
    };
  }, [pdfDoc]);

  // Render current page with proper cancellation
  const renderPage = useCallback(async (pageNum, renderScale) => {
    if (!pdfDoc || !canvasRef.current) return;

    // If already rendering, queue this render
    if (renderingRef.current) {
      pendingRenderRef.current = { pageNum, renderScale };
      return;
    }

    renderingRef.current = true;

    try {
      await renderPdfPage(pdfDoc, pageNum, canvasRef.current, renderScale, textLayerRef.current);
    } catch (err) {
      // Ignore cancellation errors
      if (err.name !== 'RenderingCancelledException') {
        console.error('Error rendering page:', err);
        setError(err.message);
      }
    } finally {
      renderingRef.current = false;
      
      // Process any pending render
      if (pendingRenderRef.current) {
        const pending = pendingRenderRef.current;
        pendingRenderRef.current = null;
        // Use setTimeout to avoid synchronous recursion
        setTimeout(() => renderPage(pending.pageNum, pending.renderScale), 0);
      }
    }
  }, [pdfDoc]);

  // Render when page or scale changes
  // Only render when scale is determined (not null)
  useEffect(() => {
    if (pdfDoc && currentPage && scale !== null) {
      renderPage(currentPage, scale);
    }
  }, [pdfDoc, currentPage, scale, renderPage]);

  const goToPage = useCallback((pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  }, [totalPages]);

  const nextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const prevPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const zoomIn = useCallback(() => {
    setScale(prev => {
      if (prev == null) return prev;
      const step = (autoScaleRef.current || 1) * 0.1; // 10% of fit-scale
      return Math.min(prev + step, 6.0);
    });
  }, []);

  const zoomOut = useCallback(() => {
    setScale(prev => {
      if (prev == null) return prev;
      const step = (autoScaleRef.current || 1) * 0.1; // 10% of fit-scale
      const minScale = Math.max((autoScaleRef.current || 0.5) * 0.5, 0.35);
      return Math.max(prev - step, minScale);
    });
  }, []);

  const resetZoom = useCallback(() => {
    if (autoScale) {
      setScale(autoScale * BASELINE_SCALE_FACTOR);
    } else {
      setScale(BASELINE_SCALE_FACTOR);
    }
  }, [autoScale]);

  // Display percentage relative to the baseline above (that scale reads as 100%).
  const defaultScale = autoScale ? autoScale * BASELINE_SCALE_FACTOR : null;
  const displayPercent = scale !== null && defaultScale
    ? Math.round((scale / defaultScale) * 100)
    : (scale !== null ? Math.round(scale * 100) : null);

  return {
    pdfDoc,
    pdfLibDoc,
    pdfBytes,
    currentPage,
    totalPages,
    scale,
    displayPercent,
    loading,
    error,
    canvasRef,
    containerRef,
    textLayerRef,
    goToPage,
    nextPage,
    prevPage,
    zoomIn,
    zoomOut,
    resetZoom
  };
};
